import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { X, Loader2, Lock } from 'lucide-react';

/**
 * Plays a replay. The source is fetched from the server at open time, so a
 * recording never sits in the page for somebody who has not earned it.
 * An uploaded file plays in a <video>; a provider link plays in an iframe.
 */
export const ReplayPlayer = ({ replayId, title, onClose }) => {
  const [source, setSource] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/replays/${replayId}/watch`);
        if (alive) setSource(data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || 'That recording could not be opened');
      }
    })();
    return () => { alive = false; };
  }, [replayId]);

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/90 p-4"
         data-testid="replay-player">
      <div className="w-full max-w-5xl">
        <div className="flex items-center justify-between gap-4 mb-3">
          <h3 className="text-base md:text-lg font-semibold text-white truncate">
            {source?.title || title}
          </h3>
          <button onClick={onClose} className="text-white/50 hover:text-white transition-colors"
                  data-testid="replay-player-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="aspect-video w-full overflow-hidden rounded-xl border border-white/10 bg-black">
          {error ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center"
                 data-testid="replay-player-error">
              <Lock className="w-7 h-7 text-white/40" />
              <p className="text-sm text-white/70">{error}</p>
            </div>
          ) : !source ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-white/40" />
            </div>
          ) : source.kind === 'file' ? (
            <video src={source.url} controls autoPlay playsInline controlsList="nodownload"
                   className="h-full w-full" data-testid="replay-video" />
          ) : (
            <iframe src={source.url} title={source.title} allowFullScreen
                    className="h-full w-full" data-testid="replay-iframe" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ReplayPlayer;
